/* prepaidTermination.js -- THE ONE OWNER of "a prepaid vendor's End Month terminates it".

   THE RULE (Brian, 2026-09-24 -- the churn rule, W-005's cancellation-at-renewal, applied to prepaids):
     The contract IN PROGRESS runs out: it was already paid, it amortises normally -- no credit, no write-off.
     Every renewal after it is dropped. Brian's example: close 2023-03, a quarterly that started 2023-02
     (history) terminated in March => the February quarter runs Feb-Apr and the May and August renewals are
     dropped.
   EXACTLY: for a vendor with End Month E, a linked contract is dropped when its start >= E. A renewal that
     STARTS in the end month is dropped: the end month means "stop after here", and a new commitment starting
     in it is not taken on.
   THE HISTORY GUARD: a contract whose start is at or before the close month is NEVER dropped, whatever E is.
     It already happened -- its payment is inside the balance the forecast re-seats from
     (bs_actuals[prepaid leaf][cutoff]). Dropping it would remove only its forecast amortisation and strand its
     unamortised balance on the balance sheet for good (measured 3,249.98 on cid=36 in the Phase 0,
     diffs/prepaid-term-phase0.txt, arm A-DEL-CURRENT).

   ONE OWNER, TWO READERS:
     - runFullModel applies activePrepaidContracts() once, BEFORE both the prepaid balance roll (section 12) and
       the prepaid expense (section 12b), so the balance sheet and the income statement read the SAME set.
     - the Vendor Dashboard overlay and the Scenarios vendor panel read prepaidForecastByVendor(), which applies
       the same filter, so what those screens show is what the model books.
     A scenario terminates a prepaid vendor by setting End Month on its vendor CLONE; nothing deletes a row.

   THE VENDOR LINK is the shared identity resolver (prepaidVendorMatch.js): the stable vendor_id first, then
   name + line -- never a name match alone. Callers pass the vendor line in the IS-leaf SLUG vocabulary
   (lineOf) and the label map, exactly as section 12b does.

   PAYABLES CONVENTION (deliberate, Brian 2026-09-24): A/P is the DPO rate across ALL vendor spend, prepaid
   amortisation included -- his spreadsheet does the same and the model ties to it. So terminating a prepaid
   also lowers A/P by that vendor's amortisation, as the spreadsheet would. That is not a defect. */
import { buildPrepaidVendorIndex, resolvePrepaidVendor } from './prepaidVendorMatch';
import { resolvePrepaidContractLine } from './prepaidRouting';

/* A vendor's End Month as 'YYYY-MM', or null for no termination. Reads end_date (the stored / engine shape)
   or endDate (the Vendor Dashboard shape). 'until_canceled', null, '' and anything not a month are null. */
export function prepaidEndMonth(v) {
  if (!v) return null;
  const e = v.end_date != null ? v.end_date : v.endDate;
  if (typeof e !== 'string') return null;
  const m = /^(\d{4}-\d{2})/.exec(e.trim());
  return m ? m[1] : null;
}

/* The contracts the model books, after End Months are applied.
     contracts : prepaid contract rows (the GET shape: start, vendorId / vendor_id, account, is_line, amort)
     vendors   : the vendor rows, in the caller's shape
     opts.cutoff      the close month; a contract with start <= cutoff is never dropped (the history guard)
     opts.lineOf      (REQUIRED) v => the vendor's IS-leaf slug (v.is_line on the engine side, v.isCat on the Dashboard)
     opts.labelToLine buildPrepaidLabelToLine(isLines) -- the contract side of the name + line tier
   Returns the SAME array when no linked vendor carries an End Month (the byte-identical path), else a new
   array holding the same contract objects minus the dropped ones. */
export function activePrepaidContracts(contracts, vendors, opts) {
  const list = contracts || [];
  const o = opts || {};
  if (!(vendors || []).some(v => prepaidEndMonth(v))) return list;
  const cutoff = o.cutoff;
  if (!cutoff) return list;                      // no close month => nothing can be told apart from history
  const index = buildPrepaidVendorIndex(vendors, { lineOf: o.lineOf });
  const contractLine = c => resolvePrepaidContractLine(c, o.labelToLine || new Map()).lineKey;
  return list.filter(c => {
    if (!c || !c.start || !(c.start > cutoff)) return true;          // HISTORY GUARD: started at or before the close
    const vendor = resolvePrepaidVendor(c, index, { lineOf: contractLine }).vendor;
    const E = prepaidEndMonth(vendor);
    return !(E && c.start >= E);                                     // a renewal starting in or after E is dropped
  });
}

/* Per-vendor forecast amortisation of the contracts the model books -- the Vendor Dashboard overlay's shape.
   Returns { amortByVendorId: { [vendorId]: { [month]: amort } }, countByVendorId: { [vendorId]: n } }.
     countByVendorId counts every LINKED stored contract (a display chip), dropped renewals included.
     amortByVendorId sums only the active contracts that route to an IS leaf, forecast months only (m > cutoff),
       which is what section 12b books (a contract that routes nowhere books $0 there, so it adds nothing here). */
export function prepaidForecastByVendor(contracts, vendors, opts) {
  const o = opts || {};
  const index = buildPrepaidVendorIndex(vendors || [], { lineOf: o.lineOf });
  const labelToLine = o.labelToLine || new Map();
  const contractLine = c => resolvePrepaidContractLine(c, labelToLine).lineKey;
  const live = new Set(activePrepaidContracts(contracts, vendors, o));
  const amortByVendorId = {};
  const countByVendorId = {};
  (contracts || []).forEach(c => {
    const lv = resolvePrepaidVendor(c, index, { lineOf: contractLine }).vendor;
    if (!lv) return;
    countByVendorId[lv.id] = (countByVendorId[lv.id] || 0) + 1;
    if (!live.has(c)) return;
    if (contractLine(c) == null) return;
    Object.entries(c.amort || {}).forEach(([m, amt]) => {
      if (m <= o.cutoff) return;
      if (!amortByVendorId[lv.id]) amortByVendorId[lv.id] = {};
      amortByVendorId[lv.id][m] = Math.round(((amortByVendorId[lv.id][m] || 0) + amt) * 100) / 100;
    });
  });
  return { amortByVendorId, countByVendorId };
}
